import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api/endpoints';
import type { FnbItem, FnbVendor } from '../api/types';
import { formatMoney } from '../lib/format';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

// Money is typed as digits and an optional decimal point, nothing else. The
// field stays a string until submit so a half-typed "40." is not mangled.
const cleanPrice = (value: string) => value.replace(/[^0-9.]/g, '');
const parsePrice = (value: string) => {
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

// The filter chips. 'all' shows everything; 'none' is the counter's own stock.
type VendorFilter = 'all' | 'none' | string;
const OTHER = 'Other';

function ItemFormDialog({
  item,
  vendors,
  categoriesByVendor,
  defaultVendorId,
  onClose,
  onSaved,
}: {
  item: FnbItem | null;
  vendors: FnbVendor[];
  /** Existing sections per vendor, offered as suggestions so "Frankies" and
   *  "Frankie" do not end up as two headings on the station menu. */
  categoriesByVendor: Map<string, string[]>;
  defaultVendorId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(item?.name ?? '');
  const [vendorId, setVendorId] = useState<string>(item ? (item.vendor_id ?? '') : (defaultVendorId ?? ''));
  const [category, setCategory] = useState(item?.category ?? '');
  const [purchase, setPurchase] = useState(
    item?.purchase_price !== undefined ? String(item.purchase_price) : '',
  );
  const [selling, setSelling] = useState(item ? String(item.selling_price) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const purchasePrice = parsePrice(purchase);
  const sellingPrice = parsePrice(selling);
  const valid = name.trim().length > 0 && purchasePrice !== null && sellingPrice !== null;
  // Not blocked — selling at or below cost can be deliberate (a promotion, a
  // clearance) — but worth a glance before saving.
  const belowCost =
    purchasePrice !== null && sellingPrice !== null && sellingPrice < purchasePrice;
  const suggestions = categoriesByVendor.get(vendorId) ?? [];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const input = {
        name: name.trim(),
        purchase_price: purchasePrice,
        selling_price: sellingPrice,
        vendor_id: vendorId || null,
        category: category.trim() || null,
      };
      if (item) {
        await api.fnb.updateItem(item.item_id, input);
        toast.success(`${input.name} updated.`);
      } else {
        await api.fnb.createItem(input);
        toast.success(`${input.name} added to the menu.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={item ? `Edit ${item.name}` : 'New menu item'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Classic Veg Frankie (Cheese)"
          hint='Put a variant in brackets, e.g. "(Cheese)" — the station menu shows it as an option next to the regular one.'
          required
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Vendor"
            value={vendorId}
            onChange={(event) => setVendorId(event.target.value)}
          >
            <option value="">Counter (no vendor)</option>
            {vendors.map((vendor) => (
              <option key={vendor.vendor_id} value={vendor.vendor_id}>
                {vendor.name}
                {vendor.is_active ? '' : ' (off)'}
              </option>
            ))}
          </Select>
          <Input
            label="Section"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder="Frankies"
            list="menu-category-suggestions"
            hint="Heading on the station menu. Blank = Other."
          />
          <datalist id="menu-category-suggestions">
            {suggestions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          <Input
            label="Purchase price (₹)"
            value={purchase}
            onChange={(event) => setPurchase(cleanPrice(event.target.value))}
            inputMode="decimal"
            placeholder="28"
            hint="What the cafe pays. Never shown to customers."
            required
          />
          <Input
            label="Selling price (₹)"
            value={selling}
            onChange={(event) => setSelling(cleanPrice(event.target.value))}
            inputMode="decimal"
            placeholder="40"
            hint="What goes on the customer's tab."
            required
          />
        </div>

        {belowCost && (
          <p className="text-sm text-amber-300">
            Selling price is below cost — the cafe loses{' '}
            {formatMoney(purchasePrice - sellingPrice)} on each one.
          </p>
        )}

        {/* Editing a price changes the next order only. Saying so here stops an
            admin expecting an open tab to move. */}
        {item && (
          <p className="text-xs text-slate-500">
            Items already on a customer's tab keep the price they were sold at.
          </p>
        )}

        <Button type="submit" className="w-full" disabled={busy || !valid}>
          {busy ? 'Saving…' : item ? 'Save changes' : 'Add to menu'}
        </Button>
      </form>
    </Modal>
  );
}

function VendorFormDialog({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: FnbVendor | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(vendor?.name ?? '');
  const [phone, setPhone] = useState(vendor?.phone ?? '');
  const [order, setOrder] = useState(vendor ? String(vendor.display_order) : '');
  const [commission, setCommission] = useState(
    vendor?.commission_percent != null ? String(vendor.commission_percent) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const orderValue = order.trim() === '' ? undefined : Number(order);
  // Blank = a resale vendor; reports then count the full selling price.
  const commissionValue = commission.trim() === '' ? null : Number(commission);
  const commissionValid =
    commissionValue === null || (Number.isFinite(commissionValue) && commissionValue >= 0 && commissionValue <= 100);
  const valid = name.trim().length > 0 && commissionValid;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const input = {
        name: name.trim(),
        phone: phone.trim() || null,
        ...(orderValue !== undefined ? { display_order: orderValue } : {}),
        commission_percent: commissionValue,
      };
      if (vendor) {
        await api.fnb.updateVendor(vendor.vendor_id, input);
        toast.success(`${input.name} updated.`);
      } else {
        await api.fnb.createVendor(input);
        toast.success(`${input.name} added as a vendor.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={vendor ? `Edit ${vendor.name}` : 'New vendor'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="One More Bite"
          hint="Customers see this as a menu to pick at the station."
          required
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            inputMode="tel"
            placeholder="7778938955"
            hint="For staff calling in an order."
          />
          <Input
            label="Position"
            value={order}
            onChange={(event) => setOrder(event.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            placeholder={vendor ? undefined : 'Last'}
            hint="Lower numbers show first."
          />
        </div>
        <Input
          label="Commission %"
          value={commission}
          onChange={(event) => setCommission(event.target.value.replace(/[^0-9.]/g, ''))}
          inputMode="decimal"
          placeholder="Leave blank if the cafe buys and resells"
          hint={
            commissionValid
              ? 'Only for a kitchen that pays the cafe a cut. Reports count just this share of each sale as food revenue. Changes apply to items added to tabs from now on.'
              : 'Enter a percentage from 0 to 100.'
          }
        />
        <Button type="submit" className="w-full" disabled={busy || !valid}>
          {busy ? 'Saving…' : vendor ? 'Save changes' : 'Add vendor'}
        </Button>
      </form>
    </Modal>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => Promise<string>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      toast.success(await onConfirm());
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-sm text-slate-300">{body}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="danger" className="flex-1" disabled={busy} onClick={confirm}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Keep it
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function VendorsCard({
  vendors,
  onAdd,
  onEdit,
  onToggle,
  onDelete,
  busyId,
}: {
  vendors: FnbVendor[];
  onAdd: () => void;
  onEdit: (vendor: FnbVendor) => void;
  onToggle: (vendor: FnbVendor) => void;
  onDelete: (vendor: FnbVendor) => void;
  busyId: string | null;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">Vendors</h2>
          <p className="text-xs text-slate-500">
            The kitchens the food comes from. Customers pick one at the station. Switch a vendor
            off to hide its menu for the day.
          </p>
        </div>
        <Button variant="secondary" onClick={onAdd}>
          Add vendor
        </Button>
      </div>
      {vendors.length === 0 ? (
        <EmptyState title="No vendors yet.">
          Add the kitchens you order from, then give each menu item its vendor.
        </EmptyState>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-slate-800">
            {vendors.map((vendor) => (
              <li
                key={vendor.vendor_id}
                className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 ${
                  vendor.is_active ? '' : 'opacity-60'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-medium text-slate-100">
                    <span className="truncate">{vendor.name}</span>
                    {!vendor.is_active && <Badge tone="amber">Off</Badge>}
                    {vendor.commission_percent != null && (
                      <Badge tone="sky">{vendor.commission_percent}% commission</Badge>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {vendor.item_count} item{vendor.item_count === 1 ? '' : 's'}
                    {vendor.phone && ` · ${vendor.phone}`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => onEdit(vendor)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    disabled={busyId === vendor.vendor_id}
                    onClick={() => onToggle(vendor)}
                  >
                    {vendor.is_active ? 'Switch off' : 'Switch on'}
                  </Button>
                  {vendor.item_count === 0 && (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                      onClick={() => onDelete(vendor)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

type Group = { key: string; title: string; off: boolean; sections: [string, FnbItem[]][] };

// Vendor, then section — the order the station menu shows them in. The server
// already sorts this way; grouping keeps that order.
function groupItems(items: FnbItem[]): Group[] {
  const groups: Group[] = [];
  for (const item of items) {
    const key = item.vendor_id ?? 'none';
    let group = groups.find((entry) => entry.key === key);
    if (!group) {
      group = {
        key,
        title: item.vendor_name ?? 'Counter (no vendor)',
        off: !item.vendor_active,
        sections: [],
      };
      groups.push(group);
    }
    const section = item.category ?? OTHER;
    let bucket = group.sections.find(([name]) => name === section);
    if (!bucket) {
      bucket = [section, []];
      group.sections.push(bucket);
    }
    bucket[1].push(item);
  }
  return groups;
}

export function MenuAdminPage() {
  const toast = useToast();
  const [items, setItems] = useState<FnbItem[] | null>(null);
  const [vendors, setVendors] = useState<FnbVendor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<VendorFilter>('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FnbItem | null>(null);
  const [deleting, setDeleting] = useState<FnbItem | null>(null);
  const [vendorForm, setVendorForm] = useState<FnbVendor | 'new' | null>(null);
  const [deletingVendor, setDeletingVendor] = useState<FnbVendor | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [menu, kitchens] = await Promise.all([api.fnb.items(), api.fnb.vendors()]);
      setItems(menu);
      setVendors(kitchens);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categoriesByVendor = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const item of items ?? []) {
      if (!item.category) continue;
      const key = item.vendor_id ?? '';
      const list = map.get(key) ?? [];
      if (!list.includes(item.category)) list.push(item.category);
      map.set(key, list);
    }
    return map;
  }, [items]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (items ?? []).filter((item) => {
      if (filter === 'none' && item.vendor_id !== null) return false;
      if (filter !== 'all' && filter !== 'none' && item.vendor_id !== filter) return false;
      return !needle || item.name.toLowerCase().includes(needle);
    });
  }, [items, filter, search]);

  const toggleVendor = async (vendor: FnbVendor) => {
    setTogglingId(vendor.vendor_id);
    try {
      await api.fnb.updateVendor(vendor.vendor_id, { is_active: !vendor.is_active });
      toast.success(
        vendor.is_active
          ? `${vendor.name} is switched off. Customers no longer see its menu at the stations.`
          : `${vendor.name} is back on the station menu.`,
      );
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setTogglingId(null);
    }
  };

  const hasUnassigned = (items ?? []).some((item) => item.vendor_id === null);
  const chip = (value: VendorFilter, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setFilter(value)}
      className={`min-h-9 rounded-full border px-3 text-sm transition-colors ${
        filter === value
          ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
          : 'border-slate-800 text-slate-300 hover:bg-slate-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Food &amp; drinks menu</h1>
        <p className="text-sm text-slate-500">
          Staff add these to a customer's tab, and customers order them from their station.
          Customers are charged the selling price.
        </p>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {items === null && !error ? (
        <Spinner label="Loading the menu…" />
      ) : (
        <>
          <VendorsCard
            vendors={vendors}
            busyId={togglingId}
            onAdd={() => setVendorForm('new')}
            onEdit={setVendorForm}
            onToggle={toggleVendor}
            onDelete={setDeletingVendor}
          />

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-200">Items</h2>
              <Button onClick={() => setCreating(true)}>New item</Button>
            </div>

            {items && items.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {chip('all', 'All')}
                {vendors.map((vendor) => chip(vendor.vendor_id, vendor.name))}
                {hasUnassigned && chip('none', 'No vendor')}
                <div className="ml-auto w-full sm:w-64">
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search items…"
                  />
                </div>
              </div>
            )}

            {items && items.length === 0 ? (
              <EmptyState title="The menu is empty.">
                Add a vendor, then the snacks and drinks it sells, with what they cost and what
                they sell for.
              </EmptyState>
            ) : visible.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">Nothing matches.</p>
            ) : (
              groupItems(visible).map((group) => (
                <Card key={group.key} className="p-0">
                  <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
                    <h3 className="font-semibold text-slate-100">{group.title}</h3>
                    {group.off && <Badge tone="amber">Off — hidden at stations</Badge>}
                  </div>
                  {group.sections.map(([section, sectionItems]) => (
                    <div key={section}>
                      <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 bg-slate-900/80 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:grid-cols-[1fr_6rem_6rem_6rem_auto]">
                        <span className="text-sky-300/80">{section}</span>
                        <span className="text-right">Cost</span>
                        <span className="text-right">Sells for</span>
                        <span className="hidden text-right sm:block">Margin</span>
                        <span />
                      </div>
                      {/* A table, not cards: the admin is comparing prices down a column. */}
                      <ul className="divide-y divide-slate-800">
                        {sectionItems.map((item) => {
                          const cost = item.purchase_price ?? 0;
                          const margin = item.selling_price - cost;
                          return (
                            <li
                              key={item.item_id}
                              className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-4 py-3 sm:grid-cols-[1fr_6rem_6rem_6rem_auto]"
                            >
                              <p className="truncate font-medium text-slate-100">{item.name}</p>
                              <p className="text-right text-sm text-slate-400">{formatMoney(cost)}</p>
                              <p className="text-right text-sm font-semibold text-slate-100">
                                {formatMoney(item.selling_price)}
                              </p>
                              <p
                                className={`hidden text-right text-sm sm:block ${
                                  margin < 0 ? 'text-amber-300' : 'text-emerald-300'
                                }`}
                              >
                                {margin < 0 ? `−${formatMoney(-margin)}` : formatMoney(margin)}
                              </p>
                              <div className="col-span-4 mt-2 flex justify-end gap-1 sm:col-span-1 sm:mt-0">
                                <Button
                                  variant="ghost"
                                  className="px-2 py-1 text-xs"
                                  onClick={() => setEditing(item)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                                  onClick={() => setDeleting(item)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </Card>
              ))
            )}
          </section>
        </>
      )}

      {(creating || editing) && (
        <ItemFormDialog
          item={editing}
          vendors={vendors}
          categoriesByVendor={categoriesByVendor}
          defaultVendorId={filter !== 'all' && filter !== 'none' ? filter : null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={`Remove ${deleting.name}?`}
          body="Staff will no longer be able to add it to a tab, and customers will not see it at the stations. Anything already on a customer's tab — paid or not — is unaffected."
          confirmLabel="Remove from menu"
          onConfirm={async () => {
            await api.fnb.deleteItem(deleting.item_id);
            await load();
            return `${deleting.name} removed from the menu.`;
          }}
          onClose={() => setDeleting(null)}
        />
      )}
      {vendorForm && (
        <VendorFormDialog
          vendor={vendorForm === 'new' ? null : vendorForm}
          onClose={() => setVendorForm(null)}
          onSaved={load}
        />
      )}
      {deletingVendor && (
        <ConfirmDialog
          title={`Delete ${deletingVendor.name}?`}
          body="This vendor has no items, so it can be removed completely."
          confirmLabel="Delete vendor"
          onConfirm={async () => {
            await api.fnb.deleteVendor(deletingVendor.vendor_id);
            if (filter === deletingVendor.vendor_id) setFilter('all');
            await load();
            return `${deletingVendor.name} removed.`;
          }}
          onClose={() => setDeletingVendor(null)}
        />
      )}
    </div>
  );
}
